<!--

ViewEdit.vue
============

Used to create a panel with text, that can then be edited.
To use this add the viewEdit/ViewEdit.vue component and call it with:

<view-edit v-model="model" // data to show and modify
           :comparison // data (same class as model) to use to show comparisons when editing
           :layout="layout" // the layout for the view and edit
           :edit-layout="layout" // the layout for editing (layout is used if this is not supplied)
           :edit-mode-on="editModeOn" // true=edit mode, false=view mode (default false)
           :sending="sending" // true=update in progress, false=waiting (default false). Used to control an update button
           :errorText="error" // String showing an error. Shown if non empty or null
           :editMenu="menu items" // array of menu items to be shown via a triple dot button on the edit page
           :prefill="model" // object to use in edit mode.
           @cancel-pressed="cancelPressed" // event emitted when the "Go Back" button is pressed
           @update:modelValue="model updated" // event emitted when the "Update" button is pressed and the model is updated
           @delete-pressed="deletePressed" /> // event emitted when the "Delete" button is pressed

* model is the data to work with
* layout is how the data is to be presented. The object description is below
* edit-mode-on is a bool flag to indicate whether we are viewing (false) or editing (true)
* @cancel-pressed is called when the user, in edit mode, clicked cancel (Go Back)
* @update:modelValue is called when the model is updated, which happens when the user, in edit mode, clicked "Update"

The layout object has the following format:
{
    id: [text], // id of component (Mandatory)
    editTitle: [text], // title to show on edit dialog (Mandatory)
    editSize: [class name], // dialog size class
    inlineDisplay: [true|false], // should the items be shown in line, or in a column
    showDelete: [true|false], // true=put a delete button on edit mode (default false)
    verticalTabDirection: [true|false], // if true, the fields tab down the columns, otherwise they tab across the columns in a row (default false)

    rows: [
        {
            style: [style for row], // a style string to use on the row
            columnClass: [column class name], // The name of the column class. defaults to "col".
            title: [name for row], // text to place above the columns, centred in the row
            columns: [
                {
                    width: <0-12>, // 0 = unset. This takes precedent over columnClass.
                    title: <text>, // title of element (Mandatory)
                    subTitle: <text>, // sub title when two title are used (checkbox, for example)
                    editType: <type of edit box>, // the "type" in an html input * (Mandatory)
                    attributeName: <text>/<function>, // name of the attribute in the object (or function for button) (Mandatory)
                    arrayIndex: <number>, // index used if attributeName is an array. Leave undefined if attributeName does not refer to an array
                    dropDownItems: <id /title array>, // array of id/title pairs for a type of "dropdown",
                    falseTrueNames: <[<false name>,<true name>]>, array of names for the true and false case.
                    viewClass: <string>, class used when viewing.
                    outline: <string>, outline specification used for viewing
                    backgroundColor: <string>, background color used for both viewing and editing,
                    inlineDisplay: shows the items inline ([title]: [content], instead of as heading/body (default false),
                    formatMessage: <bool>, this is set to true to format square bracket based on the key word in it (default false)
                    required: <bool>, this field is required. If it is not filled in, an error is shown
                    typeahead: <string array>, if supplied, the array of strings will be used in a typeahead (show strings that match the characters already entered).
                }, ...
            ]
        }, ...
    ]
}

* editType -
 edit type is the type for a simple input, or it can be
 - [input types] : any input type, like "text", "number", "date" or "checkbox"
 - "textarea" for a textarea edit control and multi-line view
 - "dropdown" for a select list. This requires "dropDownItems" to be populated. The view is the current value from the list
 - "resourceImage" for a resource image. This will come from the organisations "Images" resource
 - "mp4VideoLink" for a link to an mp4 video. In edit this is a simple text field, in view it is a player
 - "embeddedVideoLink" for a link to an embedded video (probably YouTube). In edit this is a simple text field, in view it is a player
 - "vimeoLink" for a link to a Vimeo video. In edit this is a simple text field, in view it is a player
 - "richtext" for a rich text editor and viewer
 - "button" to show a button. Attribute name will now be a callback function name. className needs to have the icon class to use (e.g. "icon-pencil")
 - "slider" for a slider instead of checkbox.

* function
    setEditData(data) - takes the same data type as v-model. This fills the edit page, instead of using the modelValue
    closeEditMode - close the edit mode. Used if the ViewEdit was on auto edit, and was removed before it can close
-->



<template>
    <!-- Always visible View section -->
    <div :style="dispStyle">
        <view-edit-view :data="data"
                        :layout="layout" />
    </div>

    <!-- Dialog based Edit section -->
    <template v-if="showEdit">
        <view-edit-dialog :ref="editDialog"
                          v-model="viewEditData"
                          :id-val="dialogId"
                          :comparison="comparison"
                          :layout="layoutForEditing"
                          :visibility="visibility"
                          @cancel-pressed="cancelPressed"
                          @update-pressed="updatePressed"
                          @delete-pressed="deletePressed" />
    </template>
</template>
<script setup>
    import { ref, computed, defineProps, defineEmits, defineExpose, watch, onMounted, nextTick, defineAsyncComponent } from 'vue';
    import { Tooltip } from 'bootstrap';
    import { generateData } from '../../js/ViewEditUtilities.js';
    const ContextButton = defineAsyncComponent(() => import('../ContextButton.vue'));
    const ViewEditView = defineAsyncComponent(() => import('./impl/ViewEditView.vue'));
    const ViewEditDialog = defineAsyncComponent(() => import('./impl/ViewEditDialog.vue'));

    const props = defineProps({
        modelValue: {
            type: Object,
            default: null
        },
        comparison: {
            type: Object,
            default: null
        },
        layout: {
            type: Object,
            default: null
        },
        editLayout: {
            type: Object,
            default: null
        },
        editModeOn: {
            type: Boolean,
            default: false
        },
        sending: {
            type: Boolean,
            default: false
        },
        errorText: {
            type: String,
            default: null
        },
        editMenu: {
            type: Array,
            default: null
        },
        prefill: {
            type: Object,
            default: null
        },
        visibility: {
            type: Object,
            default: null
        }
    });

    const emit = defineEmits(['update:modelValue', 'cancelPressed', 'updatePressed', 'deletePressed']);

    defineExpose({
        setEditData,
        closeEditMode
    });

    onMounted(() => {
        new Tooltip(document.body, {
            selector: "[data-bs-toggle='tooltip']",
        });

        if (props.editModeOn) { // started with edit on
            if (!prefillVal.value) {
                prefillVal.value = props.prefill;
            };
        }
    });

    const viewEditData = computed({
        get: () => {
            return props.modelValue;
        },
        set: (newval) => {
            emit('update:modelValue', newval);
        }
    });

    const data = computed(() => generateData(props.modelValue, props.layout));
    const showEdit = computed(() => props.editModeOn || editTurnedOn.value);
    const layoutForEditing = computed(() => props.editLayout ? props.editLayout : props.layout);
    const prefillVal = ref(null); // used to prefill the edit
    const editTurnedOn = ref(false); // used when manually turning edit on or off, not using property

    function cancelPressed() {
        emit('cancelPressed');
    }

    function deletePressed() {
        emit('deletePressed');
    }

    function openEditMode() {
        editTurnedOn.value = true;
    }

    function closeEditMode() {
        editTurnedOn.value = false;
    }

    function contextMenu(id) {
        return props.editMenu;
    }

    function setEditData(data) {
        prefillVal.value = data;
        // global fix?
        //emit('update:modelValue', data);
    }

</script>
